import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { useAuth } from "./auth";

export type PermissionKey =
  | "manual_product_create"
  | "manual_product_delete"
  | "production_manage"
  | "packaging_manage"
  | "catalog_admin"
  | "user_manage"
  | "location_manage";

type Me = {
  username: string;
  is_admin: boolean;
  permissions: PermissionKey[];
};

// Resolves the caller's effective permissions from /api/auth/me. Admins get an
// implicit code-level bypass server-side, surfaced here as is_admin so the UI
// can treat every gate as granted without listing the keys.
export function usePermissions() {
  const access = useAuth((s) => s.access);
  const { data, isLoading } = useQuery<Me>({
    queryKey: ["me", "permissions"],
    queryFn: async () => (await api.get("/api/auth/me")).data,
    enabled: !!access,
    staleTime: 5 * 60_000,
  });

  const isAdmin = data?.is_admin ?? false;
  const granted = data?.permissions ?? [];

  return {
    isAdmin,
    permissions: granted,
    isLoading,
    hasPermission: (key: PermissionKey) => isAdmin || granted.includes(key),
  };
}
